/**
 * VideoWidget — a content-addressed video (contentType "video"). Same asset
 * pipeline as ImageWidget: a picked file is hashed + written to OPFS with no
 * network (opfsAssetStore), the item stores only its `sha256`, and display
 * resolves OPFS → peer (libp2p) → cloud. Publish ships the bytes by sha
 * (siteAssets collects "video" items too). Renders a `<video controls>`.
 *
 * WYSIWYG: at rest / preview it's just the player; the editor adds an upload
 * placeholder + a Replace button. No crop (unlike images).
 */
import { useEffect, useRef, useState } from "react";
import { css, html } from "react-strict-dom";
import { Badge, Button, FileTrigger, Icon, Tooltip } from "@duro-app/ui";
import type { FileTriggerHandle } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { useUpdateGrid } from "@fredericrous/grid";

import type { WidgetProps } from "./types.js";
import { useSiteId } from "../SiteContext.js";
import { getAssetFile, putAsset } from "../assets/opfsAssetStore.js";
import {
  getCollabBlobResolver,
  getCollabBlobAnnounce,
} from "../assets/collabBlobResolver.js";
import {
  markBackingUp,
  useBackupStatus,
} from "../assets/collabBackupStatus.js";
import { useRoomPeerCount } from "../assets/collabPeers.js";
import { openMediaLibraryForPick } from "../mediaLibrary.js";
import { useAddDrag } from "../AddDragContext.js";
import { useMediaBindRef } from "./mediaBindRegistry.js";

const ACCEPTED = "video/mp4,video/webm,video/ogg,video/quicktime";

const styles = css.create({
  kindIcon: {
    display: "inline-flex",
    color: colors.textMuted,
  },
  // 80% of the tile, clamped — see ImageWidget's KindIcon.
  kindIconFluid: {
    width: "80%",
    height: "80%",
    minWidth: sizes.iconLg,
    minHeight: sizes.iconLg,
    maxWidth: sizes.glyphClampMax,
    maxHeight: sizes.glyphClampMax,
    alignItems: "center",
    justifyContent: "center",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  // Mockup mode: the kind icon alone on a neutral fill.
  mockup: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.bgCardHover,
  },
  tile: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tileEmpty: {
    backgroundColor: colors.bgCardHover,
  },
  tileFilled: {
    backgroundColor: colors.fixedDark,
  },
  // A dragged media asset hovering an EMPTY tile: the drop-to-fill ring.
  bindTarget: {
    backgroundColor: colors.infoBg,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: colors.accent,
    borderRadius: radii.xs,
  },
  replaceSlot: {
    position: "absolute",
    top: spacing.xs,
    right: spacing.xs,
    zIndex: layers.raised,
  },
  uploadPrompt: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: microSpacing.px6,
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
    width: "100%",
    height: "100%",
    padding: spacing.sm,
    boxSizing: "border-box",
  },
  // Fluid icon area — see ImageWidget's empty placeholder.
  iconArea: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  uploading: {
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
  },
  error: {
    position: "absolute",
    bottom: spacing.xs,
    left: spacing.xs,
    color: colors.error,
    fontSize: typography.fontSizeXs,
  },
  backupSlot: {
    position: "absolute",
    top: spacing.xs,
    left: spacing.xs,
  },
  // Mounts the file picker on an empty tile, where the tile itself is the
  // trigger (its click calls the picker's open()).
  pickerHidden: {
    display: "none",
  },
});

/** The kind glyph in a box that scales with the tile — 80% of it, clamped
 *  iconLg–glyphClampMax. Duro's Icon takes a fixed size token, so the glyph
 *  itself is the largest one (xxl), centred in that box. */
function KindIcon() {
  return (
    <html.span aria-hidden style={[styles.kindIcon, styles.kindIconFluid]}>
      {/* holds-until: Duro Icon gets a fluid size (https://github.com/fredericrous/duro-design-system/issues/106); the glyph stops at 48px meanwhile */}
      <Icon name="video" size="xxl" />
    </html.span>
  );
}

/** Stops a press from reaching the tile (its click opens the picker) or the
 *  grid item (its pointer-down starts a move). Duro's Button hands its onClick
 *  no event, so the slot around it does the stopping. */
function stop(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

export function VideoWidget({ itemId, contentProps, gridId }: WidgetProps) {
  const url = contentProps?.["url"] as string | undefined;
  const sha256 = contentProps?.["sha256"] as string | undefined;
  const ext = contentProps?.["ext"] as string | undefined;
  const mime = contentProps?.["mime"] as string | undefined;
  const preview = contentProps?.["preview"] as boolean | undefined;
  const controls = (contentProps?.["controls"] as boolean | undefined) ?? true;
  const loop = !!contentProps?.["loop"];
  const autoplay = !!contentProps?.["autoplay"];
  const muted = (contentProps?.["muted"] as boolean | undefined) ?? autoplay;
  const siteId = useSiteId();
  const { updateItem } = useUpdateGrid(gridId);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [src, setSrc] = useState<string | undefined>(
    typeof url === "string" && url ? url : undefined,
  );
  const pickerRef = useRef<FileTriggerHandle>(null);
  const backup = useBackupStatus(sha256 ?? "");
  const roomPeers = useRoomPeerCount();
  const empty = !sha256 && !url;
  const isBindTarget = useAddDrag().bindTargetId === itemId;
  const bindRef = useMediaBindRef<HTMLDivElement>(
    "video",
    gridId,
    itemId,
    empty,
  );

  // Resolve the bytes: OPFS first, else a room peer (P2P), else the cloud asset
  // route once published. Mirrors ImageWidget's resolver.
  useEffect(() => {
    if (typeof url === "string" && url) {
      setSrc(url);
      return;
    }
    if (!sha256) {
      setSrc(undefined);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      const show = (file: Blob): void => {
        const typed = mime ? file.slice(0, file.size, mime) : file;
        objectUrl = URL.createObjectURL(typed);
        setSrc(objectUrl);
      };
      const file = siteId
        ? await getAssetFile(siteId, sha256, ext ?? "bin")
        : null;
      if (cancelled) return;
      if (file) return show(file);

      const resolver = siteId ? getCollabBlobResolver(siteId) : undefined;
      if (resolver) {
        const bytes = await resolver(sha256).catch(() => null);
        if (cancelled) return;
        if (bytes) {
          const peerFile = await getAssetFile(siteId!, sha256, ext ?? "bin");
          if (cancelled) return;
          if (peerFile) return show(peerFile);
        }
      }
      setSrc(siteId ? `/api/v1/assets/${siteId}/${sha256}` : undefined);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, sha256, siteId, mime, ext]);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const asset = await putAsset(siteId ?? "", file);
      const next: Record<string, unknown> = { ...(contentProps ?? {}) };
      delete next["url"];
      next["sha256"] = asset.sha256;
      next["mime"] = asset.mime;
      next["ext"] = asset.ext;
      updateItem(itemId, { contentProps: next });
      const announce = siteId ? getCollabBlobAnnounce(siteId) : undefined;
      if (announce) {
        announce(asset.sha256);
        markBackingUp(asset.sha256);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  // The file picker: the visible Replace button on a filled tile; on an empty
  // tile it stays mounted (hidden) so the tile's click can open() it.
  const picker = (
    <FileTrigger
      ref={pickerRef}
      accept={ACCEPTED}
      variant="secondary"
      size="small"
      onSelect={(files) => {
        const f = files[0];
        if (f) void handleFile(f);
      }}
    >
      Replace
    </FileTrigger>
  );

  const videoEl = src ? (
    <video
      src={src}
      controls={controls}
      autoPlay={autoplay}
      loop={loop}
      muted={muted}
      playsInline
      {...css.props(styles.video)}
    />
  ) : null;

  if (preview)
    return (
      videoEl ?? (
        // Mockup mode: the kind icon only — no upload text.
        <html.div style={styles.mockup}>
          <KindIcon />
        </html.div>
      )
    );

  return (
    <html.div
      // Empty tiles are drop-to-fill targets for a dragged video asset.
      ref={bindRef}
      style={[
        styles.tile,
        src ? styles.tileFilled : styles.tileEmpty,
        isBindTarget && styles.bindTarget,
      ]}
      onClick={(e) => {
        // Empty tile → open the picker; a filled tile leaves clicks to the
        // player controls (don't hijack scrubbing).
        if (!src && !uploading) {
          e.stopPropagation();
          pickerRef.current?.open();
        }
      }}
      onPointerDown={(e: React.PointerEvent) => {
        if (src) e.stopPropagation(); // let the player own the gesture
      }}
    >
      {videoEl}
      {src && (
        <html.span
          style={styles.replaceSlot}
          onClick={stop}
          onPointerDown={stop}
        >
          {picker}
        </html.span>
      )}
      {!src && !uploading && (
        <html.span style={styles.uploadPrompt}>
          <html.span style={styles.iconArea}>
            <KindIcon />
          </html.span>
          Click to upload video
          <html.span onClick={stop} onPointerDown={stop}>
            <Button
              variant="link"
              size="small"
              onClick={() =>
                openMediaLibraryForPick({ gridId, itemId, kind: "video" })
              }
            >
              or choose from library
            </Button>
          </html.span>
        </html.span>
      )}
      {uploading && <html.span style={styles.uploading}>Uploading…</html.span>}
      {error && <html.span style={styles.error}>{error}</html.span>}
      {backup === "backing-up" && !uploading && (
        <html.span style={styles.backupSlot}>
          <Tooltip.Root
            placement="bottom"
            content={
              roomPeers > 0
                ? "This video only exists on this device — keep this tab open until a collaborator loads it."
                : "This video only exists on this device. It backs up once another collaborator is online."
            }
          >
            <Tooltip.Trigger>
              <Badge variant="warning" size="sm">
                backing up…
              </Badge>
            </Tooltip.Trigger>
          </Tooltip.Root>
        </html.span>
      )}
      {!src && (
        <html.span style={styles.pickerHidden} onClick={stop}>
          {picker}
        </html.span>
      )}
    </html.div>
  );
}
